import {isFinanceSelfEmploymentFlow} from './statusConnectionFlow';
import {RegistrationWorkStatus} from './types';
import {RegistrationStep} from './steps';

/**
 * Состояния плитки на «Выберите статус» / «Подключим ещё?»:
 * - available — не подключён (белый)
 * - connected — подключён, не основной (жёлтый без галочки)
 * - current — основной / current (жёлтый с галочкой)
 * - pending — ждём ФНС (лососевый)
 * - pending_alfa — ждём ответ Альфа (лососевый)
 * - error_* — ошибка СМЗ (красный), только self_employment
 */
export type WorkStatusConnectionState =
	| 'available'
	| 'connected'
	| 'current'
	| 'pending'
	| 'pending_alfa'
	| 'error_need_permissions'
	| 'error_not_selfemployed'
	| 'error_sign_agreement'
	| 'error_withdrawn'
	| 'error';

export type WorkStatusConnectionStates = Partial<Record<RegistrationWorkStatus, WorkStatusConnectionState>>;

const WORK_STATUS_CONNECTION_SUBTITLE: Partial<Record<WorkStatusConnectionState, string>> = {
	connected: 'Подключён',
	current: 'Подключён',
	pending: 'Ждём ответа ФНС',
	pending_alfa: 'Ждём ответа от Альфа-Банка',
	error_need_permissions: 'Нет доступа',
	error_not_selfemployed: 'Что-то не так',
	error_sign_agreement: 'Подпишите соглашение',
	error_withdrawn: 'Статус отозван',
	error: 'Не удалось подключить',
};

export const isWorkStatusPendingState = (state: WorkStatusConnectionState | undefined): boolean => {
	return state === 'pending' || state === 'pending_alfa';
};

/** Имя партнёра на плитке СМЗ. `free_smz` — без имени. */
export const getWorkStatusPartnerLabel = (
	partner: 'alfa' | 'rocket' | 'none' | undefined,
	workStatus: RegistrationWorkStatus,
): string | null => {
	if (partner === 'none') {
		return null;
	}

	if (partner === 'rocket') {
		return 'Биржа';
	}

	if (partner === 'alfa' || isFinanceSelfEmploymentFlow(workStatus)) {
		return 'Альфа-банк';
	}

	return null;
};

/** Текст состояния без имени партнёра. Имя добавляет `withWorkStatusPartnerLabel`. */
export const getWorkStatusConnectionSubtitle = (
	state: WorkStatusConnectionState | undefined,
): string | null => {
	if (!state || state === 'available') {
		return null;
	}

	return WORK_STATUS_CONNECTION_SUBTITLE[state] ?? null;
};

/** «Альфа-банк · Подключён». Если имя уже есть в статусе — не дублируем. */
export const withWorkStatusPartnerLabel = (partner: string | null, status: string | null): string | null => {
	if (!partner) {
		return status;
	}

	if (!status) {
		return partner;
	}

	if (status.toLowerCase().includes(partner.toLowerCase())) {
		return status;
	}

	return `${partner} · ${status}`;
};

export const shouldShowAlfaBankBadge = (
	workStatus: RegistrationWorkStatus,
	state: WorkStatusConnectionState | undefined,
): boolean => {
	if (!isFinanceSelfEmploymentFlow(workStatus)) {
		return false;
	}

	return !isWorkStatusErrorState(state) && !isWorkStatusPendingState(state);
};

export const isWorkStatusErrorState = (state: WorkStatusConnectionState | undefined): boolean => {
	return (
		state === 'error' ||
		state === 'error_need_permissions' ||
		state === 'error_not_selfemployed' ||
		state === 'error_sign_agreement' ||
		state === 'error_withdrawn'
	);
};

export const isWorkStatusConnectedAppearance = (state: WorkStatusConnectionState | undefined): boolean => {
	return state === 'connected' || state === 'current';
};

export const hasWorkStatusConnections = (states: WorkStatusConnectionStates | undefined): boolean => {
	if (!states) {
		return false;
	}

	return Object.values(states).some((state) => state && state !== 'available');
};

export const hasConnectedWorkStatus = (states: WorkStatusConnectionStates | undefined): boolean => {
	if (!states) {
		return false;
	}

	return Object.values(states).some((state) => state === 'connected' || state === 'current');
};

export const getWorkStatusStepTitle = (states: WorkStatusConnectionStates | undefined): string => {
	return hasConnectedWorkStatus(states) ? 'Подключим ещё?' : 'Выберите статус для работы';
};

/** Куда вести по клику на красную плитку СМЗ. */
export const getSelfEmploymentErrorResumeStep = (
	state: WorkStatusConnectionState,
): RegistrationStep | null => {
	switch (state) {
		case 'error_need_permissions':
			return RegistrationStep.MyTaxAccess;
		case 'error_not_selfemployed':
			return RegistrationStep.MyTaxNotSelfEmployed;
		case 'error_sign_agreement':
			return null;
		case 'error_withdrawn':
			return RegistrationStep.MyTaxIntro;
		case 'error':
			return RegistrationStep.MyTaxIntro;
		default:
			return null;
	}
};

/** После успешного подключения: этот статус — current, бывший current → connected. */
export const markWorkStatusAsCurrent = (
	states: WorkStatusConnectionStates,
	workStatus: RegistrationWorkStatus,
): WorkStatusConnectionStates => {
	const next: WorkStatusConnectionStates = {...states};

	(Object.keys(next) as RegistrationWorkStatus[]).forEach((key) => {
		if (next[key] === 'current') {
			next[key] = 'connected';
		}
	});

	next[workStatus] = 'current';

	return next;
};
